import { useAuth } from '../contexts/AuthContext.tsx';
import { checkPermission, hasAdminAccess, Permission, WellspringRole } from '@metron/shared';

/**
 * Hook for checking user permissions in React components.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hasPermission, isAdmin, isViewer, isSupport } = usePermissions();
 *
 *   if (!hasPermission(Permission.VIEW_USERS)) {
 *     return <div>Access Denied</div>;
 *   }
 *
 *   return <div>User list...</div>;
 * }
 * ```
 */
export function usePermissions() {
  const { user } = useAuth();
  const userGroups = user?.groups || [];

  return {
    /**
     * Check if the current user has a specific permission
     */
    hasPermission: (permission: Permission): boolean => {
      return checkPermission(userGroups, permission);
    },

    /**
     * Check if the current user has Admin-tier access (Admin or SuperAdmin)
     */
    isAdmin: hasAdminAccess(userGroups),

    /**
     * Check if the current user is a Viewer
     */
    isViewer: userGroups.includes(WellspringRole.VIEWER),

    /**
     * Check if the current user is Support
     */
    isSupport: userGroups.includes(WellspringRole.SUPPORT),

    /**
     * Get all user groups/roles
     */
    userGroups,
  };
}
